/* eslint-disable global-require */
import React, { forwardRef, useContext, useImperativeHandle, useState } from 'react'
import { ErrorBoundary } from 'react-error-boundary'
import {
	Map,
	MapProps,
	MapMouseEvent,
	AdvancedMarker,
	AdvancedMarkerProps,
	useAdvancedMarkerRef,
	useApiLoadingStatus,
	APILoadingStatus
} from '@vis.gl/react-google-maps'
import { Spinner, IconRegularError } from '@notino/react-styleguide'

// utils
import { MAP } from '../../utils/enums'
import { AppContext } from '../../utils/appProvider'

// styled
import * as SC from './GoogleMapsStyles'

// hooks
import useMessages from '../../hooks/useMessages'

// types
export type MapMarker<ExtraType extends Record<string, any> = {}> = {
	id: string
	position: AdvancedMarkerProps['position']
	extra: ExtraType
}

export type OnMarkerClickFunc<MarkerExtraType extends Record<string, any> = {}> = (
	markerElement: google.maps.marker.AdvancedMarkerElement,
	data: MapMarker<MarkerExtraType>
) => void

export type GoogleMapPosition = google.maps.LatLng | google.maps.LatLngLiteral

type Props<MarkerExtraType extends Record<string, any> = {}> = MapProps & {
	markers?: MapMarker<MarkerExtraType>[]
	defaultSelectedMarkerID?: string
	onMarkerClick?: OnMarkerClickFunc<MarkerExtraType>
	markersLoading?: boolean
}

type Handle = {
	resetCurrentMarker: () => void
}

const ErrorComponent = () => {
	const { messages } = useMessages()
	return (
		<SC.ErrorContainer>
			<IconRegularError width={'2rem'} height={'2rem'} color={'text.primary'} />
			{messages['We are sorry. Google Maps could not be loaded.']}
		</SC.ErrorContainer>
	)
}

export const AdvancedMarkerWithRef = (
	props: AdvancedMarkerProps & {
		onMarkerClick: (marker: google.maps.marker.AdvancedMarkerElement) => void
	}
) => {
	const { children, onMarkerClick, ...advancedMarkerProps } = props
	const [markerRef, marker] = useAdvancedMarkerRef()

	return (
		<AdvancedMarker
			onClick={() => {
				if (marker) {
					onMarkerClick(marker)
				}
			}}
			ref={markerRef}
			{...advancedMarkerProps}
		>
			{children}
		</AdvancedMarker>
	)
}

// component
const GoogleMaps = forwardRef(<MarkerExtraType extends Record<string, any> = {}>(props: Props<MarkerExtraType>, ref: React.Ref<Handle>) => {
	const { markersLoading, children, markers, onMarkerClick, onClick, onCameraChanged, defaultSelectedMarkerID, ...restMapProps } = props
	const { assetsPath, googleMapId } = useContext(AppContext)

	const loadingStatus = useApiLoadingStatus()

	const isMapLoaded = loadingStatus === APILoadingStatus.LOADED
	const isLoading = loadingStatus === APILoadingStatus.NOT_LOADED || loadingStatus === APILoadingStatus.LOADING || markersLoading
	const isError = loadingStatus === APILoadingStatus.FAILED || loadingStatus === APILoadingStatus.AUTH_FAILURE

	const [currentMarkerID, setCurrentMarkerID] = useState<string | null>(defaultSelectedMarkerID ?? null)

	const handleClick = (e: MapMouseEvent) => {
		if (currentMarkerID !== defaultSelectedMarkerID) {
			setCurrentMarkerID(null)
		}

		if (onClick) {
			onClick(e)
		}
	}

	useImperativeHandle(ref, () => ({
		resetCurrentMarker() {
			setCurrentMarkerID(null)
		}
	}))

	/**
	 * Note: loadingStatus from @vis.gl/react-google-maps returns states in sequence: NOT_LOADED -> LOADING -> LOADED
	 * If there's an error during map initialization, the state changes to FAILED or AUTH_FAILURE only after reaching LOADED first
	 * This transition to LOADED before error states causes issues with Advanced Markers, which throw errors during that brief LOADED period and crash the application
	 * This problem is fixed by using the <ErrorBoundary> component, which catches the error and prevents the application from crashing
	 * GitHub issue: https://github.com/visgl/react-google-maps/issues/424
	 * JIRA issue: https://goodrequest.atlassian.net/browse/NOT-11898
	 */
	return (
		<SC.MapContainer>
			<ErrorBoundary FallbackComponent={ErrorComponent}>
				{isMapLoaded && (
					<Map
						defaultZoom={MAP.defaultZoom}
						maxZoom={MAP.maxZoom}
						minZoom={MAP.minZoom}
						zoomControl={false}
						streetViewControl={false}
						fullscreenControl={false}
						mapTypeControl={false}
						clickableIcons={false} // don't allow click on google maps default pins (e.g. pins for restaurants, places, ...)
						mapId={googleMapId}
						onClick={handleClick}
						onCameraChanged={onCameraChanged}
						{...restMapProps}
					>
						{markers?.map((markerData, index) => {
							const icon =
								markerData.id === currentMarkerID
									? `${assetsPath}/${require('../../assets/icons/location-pin-icon-pink.png')}`
									: `${assetsPath}/${require('../../assets/icons/location-pin-icon-black.png')}`

							return (
								<AdvancedMarkerWithRef
									key={index}
									position={markerData.position}
									onMarkerClick={(markerElement: google.maps.marker.AdvancedMarkerElement) => {
										if (onMarkerClick) {
											onMarkerClick(markerElement, markerData)
										}
										setCurrentMarkerID(markerData.id)
									}}
								>
									<img width={36} height={44} src={icon} alt={''} />
								</AdvancedMarkerWithRef>
							)
						})}
						{/* additional map elements */}
						{children}
					</Map>
				)}
				{isLoading && (
					<SC.LoadingContainer>
						<Spinner color={'icon.highlight'} size={24} />
					</SC.LoadingContainer>
				)}
				{isError && <ErrorComponent />}
			</ErrorBoundary>
		</SC.MapContainer>
	)
}) as <MarkerExtraType extends Record<string, any> = {}>(props: Props<MarkerExtraType> & { ref?: React.Ref<Handle> }) => JSX.Element

export default GoogleMaps
